import React, { useMemo, useState } from 'react'
import {
	EuiFlexGroup,
	EuiFlexItem,
	EuiFormRow,
	EuiFieldNumber,
	EuiFieldText,
	EuiDatePicker,
	EuiButtonIcon,
	EuiPopover,
	EuiSelectable,
	EuiSelectableOption,
	EuiButtonEmpty,
	EuiButton,
	EuiSwitch,
} from '@equipmentshare/ds2'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import { useIdentity } from '@equipmentshare/auth0-react'
import { useFlags } from '@equipmentshare/feature-flags'
import moment from 'moment'
import { Part } from '../../../graphql'
import PartPicker from '../../../components/PartPicker'
import WorkOrderPicker from '../../../components/WorkOrderPicker'
import InvoicePicker from '../../../components/InvoicePicker'
import OrderPicker from '../../../components/OrderPicker'
import {
	DemandRequestLineItem,
	requiresTargetId,
	hasUnsatisfiedBusinessReason,
	NEEDS_BUSINESS_REASON_ERROR,
} from '../hooks/useDemandRequestForm'
import { DeleteBin7LineIcon, FileTextLineIcon, AddCircleLineIcon } from '@equipmentshare/ds2-icons'
import { getMinNeededByDate, filterWeekends, validateNeededByDate } from '../utils/dateValidation'
import { getLtlShipmentReason } from '../utils/ltlShipment'
import NotesModal from './NotesModal'
import { COLUMN_WIDTHS } from '../constants/columnWidths'

export type LineItemFormProps = {
	/** Line item data */
	lineItem: DemandRequestLineItem,
	/** Index of this line item in the array */
	index: number,
	/** Callback when line item is updated */
	onUpdate: ( index: number, updates: Partial<DemandRequestLineItem> ) => void,
	/** Callback when line item should be removed */
	onRemove: ( index: number ) => void,
	/** Whether this is the only line item (disable remove button) */
	isOnlyItem: boolean,
	/** Whether to show validation errors */
	showValidationErrors?: boolean,
	/** Whether the line item has validation errors */
	hasErrors?: boolean,
	/** Validation error message */
	errorMessage?: string,
	/** Branch ID to filter work orders and invoices by */
	branchId?: string,
}

type TargetTypeOption = {
	value: 'STOCK' | 'WORK_ORDER' | 'INVOICE' | 'ORDER' | '',
	label: string,
}

export default function LineItemForm( {
	lineItem,
	index,
	onUpdate,
	onRemove,
	isOnlyItem,
	showValidationErrors = false,
	branchId,
}: LineItemFormProps ) {
	const identity = useIdentity()
	const userCompanyId = identity?.companyId
	const flags = useFlags()
	const [ isTargetTypePopoverOpen, setIsTargetTypePopoverOpen ] = useState( false )
	const [ dateValidationError, setDateValidationError ] = useState<string | undefined>( undefined )
	const [ isNotesModalOpen, setIsNotesModalOpen ] = useState( false )

	const ltlShipmentReason = useMemo( () => getLtlShipmentReason( lineItem.selectedPart ), [ lineItem.selectedPart ] )

	const openTargetTypePopover = () => setIsTargetTypePopoverOpen( true )
	const closeTargetTypePopover = () => setIsTargetTypePopoverOpen( false )

	// Target type options based on company ID and feature flags
	const targetTypeOptions = useMemo( (): TargetTypeOption[] => {
		const options: TargetTypeOption[] = [
			{ value: 'STOCK', label: 'Stock' },
			{ value: 'WORK_ORDER', label: 'Work Order' }
		]
		if ( userCompanyId === 1854 ) {
			options.push( { value: 'INVOICE', label: 'Invoice' } )
		}
		if ( flags.allowCostentoryOrderAllocation ) {
			options.push( { value: 'ORDER', label: 'Order' } )
		}
		return options
	}, [ userCompanyId, flags.allowCostentoryOrderAllocation ] )

	// Get the current target type label (default to 'Stock' if empty)
	// Use abbreviated labels for the prepend element to save space
	const currentTargetTypeLabel = useMemo( () => {
		const currentType = lineItem.targetType || 'STOCK'
		switch ( currentType ) {
			case 'WORK_ORDER':
				return 'WO'
			case 'INVOICE':
				return 'INV'
			case 'ORDER':
				return 'ORD'
			case 'STOCK':
			default:
				return 'Stock'
		}
	}, [ lineItem.targetType ] )

	// Convert target type options to EuiSelectable format
	const selectableOptions = useMemo( (): EuiSelectableOption[] => {
		const currentType = lineItem.targetType || 'STOCK'
		return targetTypeOptions.map( ( option ) => ( {
			label: option.label,
			data: option,
			checked: option.value === currentType ? 'on' : undefined,
		} ) )
	}, [ lineItem.targetType, targetTypeOptions ] )

	// Event handlers
	const handlePartChange = ( partId: number, selectedPart?: Part ) => {
		onUpdate( index, {
			productId: partId ? partId.toString() : '',
			selectedPart: selectedPart ?? null
		} )
	}

	const handleQuantityChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {
		const value = parseInt( event.target.value, 10 )
		onUpdate( index, { quantity: isNaN( value ) ? 0 : value } )
	}

	const handleTargetTypeSelect = ( options: EuiSelectableOption[] ) => {
		const selectedOption = options.find( ( opt ) => opt.checked === 'on' )

		closeTargetTypePopover()

		if ( selectedOption?.data ) {
			const targetOption = selectedOption.data as TargetTypeOption
			const newTargetType = targetOption.value
			// If selecting Stock, clear targetType and targetId
			if ( newTargetType === 'STOCK' ) {
				onUpdate( index, {
					targetType: '',
					targetId: ''
				} )
			} else {
				onUpdate( index, {
					targetType: newTargetType as 'WORK_ORDER' | 'INVOICE' | 'ORDER',
					targetId: '' // Clear target ID when type changes
				} )
			}
		}
	}

	// Prepend element for the pickers
	const prependElement = useMemo( () => (
		<EuiPopover
			anchorPosition="downCenter"
			panelPaddingSize="none"
			isOpen={isTargetTypePopoverOpen}
			closePopover={closeTargetTypePopover}
			button={
				<EuiButtonEmpty
					onClick={openTargetTypePopover}
					iconType="arrowDown"
					iconSide="right"
					color="text"
				>
					{currentTargetTypeLabel}
				</EuiButtonEmpty>
			}
		>
			<EuiSelectable
				onBlur={closeTargetTypePopover}
				style={{
					width: '170px',
				}}
				listProps={{
					style: {
						textAlign: 'center',
					},
					showIcons: true,
				}}
				singleSelection
				onChange={handleTargetTypeSelect}
				options={selectableOptions}
			>
				{( list ) => list}
			</EuiSelectable>
		</EuiPopover>
	), [ isTargetTypePopoverOpen, currentTargetTypeLabel, selectableOptions, handleTargetTypeSelect ] )

	const handleWorkOrderChange = ( workOrderId: string ) => {
		onUpdate( index, { targetId: workOrderId } )
	}

	const handleInvoiceChange = ( invoiceId: string ) => {
		onUpdate( index, { targetId: invoiceId } )
	}

	const handleOrderChange = ( orderId: string ) => {
		onUpdate( index, { targetId: orderId } )
	}

	const handleNeededByDateChange = ( date: moment.Moment | null ) => {
		// Clear validation error when user changes the date
		setDateValidationError( undefined )
		onUpdate( index, { neededByDate: date ? date.format( 'YYYY-MM-DD' ) : '' } )
	}

	const handleNeededByDateBlur = () => {
		// Validate the date on blur
		if ( lineItem.neededByDate ) {
			const validation = validateNeededByDate( lineItem.neededByDate )
			if ( !validation.isValid ) {
				setDateValidationError( validation.error )
			}
		}
	}

	const handleWillCallChange = ( value: boolean ) => {
		onUpdate( index, { willCall: value } )
	}

	const handleAllowSubsChange = ( value: boolean ) => {
		onUpdate( index, { allowSubstitution: value } )
	}

	const handleRemove = () => {
		if ( !isOnlyItem ) {
			onRemove( index )
		}
	}

	const handleOpenNotesModal = () => {
		setIsNotesModalOpen( true )
	}

	const handleCloseNotesModal = () => {
		setIsNotesModalOpen( false )
	}

	const handleSaveNotes = ( notes: string ) => {
		onUpdate( index, { notes } )
	}

	// Convert date string back to moment for date picker
	const selectedDate = lineItem.neededByDate ? moment( lineItem.neededByDate ) : null

	// Expedites (next-day or 2nd-day) need a business reason
	// (Invoice/Order/WO#) - Stock (or no selection) doesn't satisfy it.
	const needsBusinessReason = hasUnsatisfiedBusinessReason( lineItem )

	const isTargetIdInvalid = requiresTargetId( lineItem.targetType ) ? !lineItem.targetId : needsBusinessReason

	// The date field itself stays selectable (this is informational, not a
	// disable) - the LTL reason takes precedence when both apply, matching the
	// existing tooltip's sole prior use.
	const dateTooltipContent = ltlShipmentReason ?? ( needsBusinessReason ? NEEDS_BUSINESS_REASON_ERROR : undefined )

	// Check if notes exist
	const hasNotes = lineItem.notes && lineItem.notes.trim().length > 0

	return (
		<>
			<EuiFlexGroup direction="row" gutterSize="s" alignItems="center" wrap={false}>
				{/* Item picker - flexible width */}
				<EuiFlexItem>
					<PartPicker
						id={`part-picker-${index}`}
						label=""
						onChange={handlePartChange}
						selectedPart={lineItem.selectedPart}
						formRowProps={{
							display: 'center',
						}}
						fullWidth
						placeholder="Select a product"
						data-testid={`part-picker-${index}`}
					/>
				</EuiFlexItem>

				{/* Quantity */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.quantity }}>
					<EuiFormRow display="center">
						<EuiFieldNumber
							id={`quantity-${index}`}
							value={lineItem.quantity || ''}
							onChange={handleQuantityChange}
							min={0}
							step={1}
							isInvalid={showValidationErrors && ( !lineItem.quantity || lineItem.quantity < 1 )}
							data-testid={`quantity-field-${index}`}
							placeholder="0"
						/>
					</EuiFormRow>
				</EuiFlexItem>

				{/* Need by Date */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.needBy }}>
					<EuiFormRow display="center">
						<Tooltip
							title={dateTooltipContent ?? ''}
							placement="top"
							arrow
							disableInteractive
						>
							<Box>
								<EuiDatePicker
									selected={selectedDate}
									onChange={handleNeededByDateChange}
									onBlur={handleNeededByDateBlur}
									placeholder="Select date"
									minDate={getMinNeededByDate()}
									filterDate={filterWeekends}
									isInvalid={!!dateValidationError || needsBusinessReason}
									disabled={!!ltlShipmentReason}
									data-testid={`needed-by-date-${index}`}
								/>
							</Box>
						</Tooltip>
					</EuiFormRow>
				</EuiFlexItem>

				{/* Allow Subs */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.allowSubs }}>
					<EuiFormRow display="center">
						<EuiSwitch
							label=""
							checked={lineItem.allowSubstitution}
							onChange={( e ) => handleAllowSubsChange( e.target.checked )}
							compressed
							data-testid={`allow-substitution-toggle-${index}`}
						/>
					</EuiFormRow>
				</EuiFlexItem>

				{/* Will Call */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.willCall }}>
					<EuiFormRow display="center">
						<EuiSwitch
							label=""
							checked={lineItem.willCall}
							onChange={( e ) => handleWillCallChange( e.target.checked )}
							compressed
							data-testid={`will-call-toggle-${index}`}
						/>
					</EuiFormRow>
				</EuiFlexItem>

				{/* Allocation (Item is for) */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.itemIsFor }}>
					{lineItem.targetType === 'WORK_ORDER' ? (
						<WorkOrderPicker
							selectedWorkOrderId={lineItem.targetId}
							onChange={handleWorkOrderChange}
							data-testid={`work-order-picker-${index}`}
							formRowProps={{
								display: 'center',
								label: '',
								labelType: 'legend',
								isInvalid: isTargetIdInvalid,
							}}
							prepend={prependElement}
							showPrefix={false}
							branchId={branchId}
						/>
					) : lineItem.targetType === 'INVOICE' ? (
						<InvoicePicker
							selectedInvoiceId={lineItem.targetId}
							onChange={handleInvoiceChange}
							data-testid={`invoice-picker-${index}`}
							formRowProps={{
								display: 'center',
								label: '',
								labelType: 'legend',
								isInvalid: isTargetIdInvalid,
							}}
							prepend={prependElement}
							branchId={branchId}
						/>
					) : lineItem.targetType === 'ORDER' ? (
						<OrderPicker
							selectedOrderId={lineItem.targetId}
							onChange={handleOrderChange}
							data-testid={`order-picker-${index}`}
							formRowProps={{
								display: 'center',
								label: '',
								labelType: 'legend',
								isInvalid: isTargetIdInvalid,
							}}
							prepend={prependElement}
							branchId={branchId}
						/>
					) : (
						<EuiFormRow display="center">
							<EuiFieldText
								disabled
								placeholder=""
								prepend={prependElement}
							/>
						</EuiFormRow>
					)}
				</EuiFlexItem>

				{/* Add Note button */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.noteButton }}>
					<EuiFormRow display="center">
						<EuiButton
							iconType={hasNotes ? FileTextLineIcon : AddCircleLineIcon}
							onClick={handleOpenNotesModal}
							color={hasNotes ? 'success' : 'primary'}
							fill={false}
							data-testid={`add-note-button-${index}`}
						>
							{hasNotes ? 'Edit Note' : 'Add Note'}
						</EuiButton>
					</EuiFormRow>
				</EuiFlexItem>

				{/* Delete button */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.deleteButton }}>
					<EuiFormRow display="center">
						<EuiButtonIcon
							iconType={DeleteBin7LineIcon}
							color="danger"
							onClick={handleRemove}
							disabled={isOnlyItem}
							aria-label={`Remove line item ${index + 1}`}
							data-testid={`remove-line-item-${index}`}
							display="base"
							size="m"
						/>
					</EuiFormRow>
				</EuiFlexItem>
			</EuiFlexGroup>

			{/* Notes Modal */}
			<NotesModal
				isOpen={isNotesModalOpen}
				onClose={handleCloseNotesModal}
				notes={lineItem.notes ?? ''}
				onSave={handleSaveNotes}
				title={`${hasNotes ? 'Edit' : 'Add'} Note - Line Item ${index + 1}`}
				placeholder="Add notes for this line item..."
			/>
		</>
	)
}
